/**
 * Projects (new) — list view (feature-flag gated on server).
 *
 * A "Project" here is the whole client build at a site (e.g. Rothschild Residence),
 * grouping multiple D-Tools job numbers into one row. Each job number is a workstream.
 *
 * Tom Clancy (2026-08-06): graduated out of beta. This is the client + site view
 * of WIP active projects.
 *
 * Backing API is /api/engagements (internal name preserved to avoid conflict with
 * the existing /api/projects routes that management/finance/WIP depend on).
 *
 * Each row links to /engagements/:id. The existing /projects (WIP) pages are unchanged.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, Stack, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon, Clear as ClearIcon, OpenInNew as OpenInNewIcon,
  Apartment as ApartmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getEngagements } from '../services/api';
import { useSortableTable } from '../utils/useSortableTable';
import PageSearchBar from '../components/PageSearchBar';

const statusColor = (s) => {
  switch (s) {
    case 'active': return 'success';
    case 'on_hold': return 'warning';
    case 'completed': return 'default';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

export default function Engagements() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  // Default sort: most workstreams first (projects with the most jobs lead).
  const { sortedRows, sortableCell } = useSortableTable(rows, {
    defaultKey: 'workstream_count',
    defaultDir: 'desc',
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getEngagements(q ? { q } : {});
      setRows(res.data.engagements || []);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setError('Projects layer is not enabled on the server. Set ENGAGEMENT_UI_ENABLED=true.');
      } else {
        setError('Failed to load projects.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearchKey = (e) => { if (e.key === 'Enter') load(); };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ApartmentIcon fontSize="large" color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">Projects</Typography>
          <Typography variant="body2" color="text.secondary">
            One row per client + site. Each project contains the job numbers (workstreams) that make it up.
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <PageSearchBar
          placeholder="Search by client, site address, or client number…"
          value={q}
          onChange={(v) => setQ(v)}
          onKeyDown={onSearchKey}
          onClear={() => { setQ(''); setTimeout(load, 0); }}
        />
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                {sortableCell('client_name', 'Project')}
                {sortableCell('client_number', 'Client #')}
                {sortableCell('site_address', 'Site')}
                {sortableCell('workstream_count', 'Workstreams', 'center')}
                <TableCell><b>Job Numbers</b></TableCell>
                {sortableCell('primary_pm_name', 'PM')}
                {sortableCell('status', 'Status')}
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No projects yet. Run <code>populate-engagements.js --apply</code> to build the initial set from D-Tools.</Typography>
                </TableCell></TableRow>
              ) : sortedRows.map((e) => (
                <TableRow key={e.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/engagements/${e.id}`)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{e.display_name || e.client_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{e.client_name}</Typography>
                  </TableCell>
                  <TableCell>{e.client_number}</TableCell>
                  <TableCell>
                    <Tooltip title={e.site_address}>
                      <Typography variant="body2" sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {e.site_address}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={e.workstream_count} size="small" color={e.workstream_count > 1 ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(e.workstreams || []).map((w) => (
                        <Chip key={w.project_id} label={w.job_number} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>{e.primary_pm_name || <em style={{ color: '#999' }}>—</em>}</TableCell>
                  <TableCell><Chip label={e.status} size="small" color={statusColor(e.status)} /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); navigate(`/engagements/${e.id}`); }}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
